/**
 * StageContextMenu — AutoCAD SHORTCUTMENU-parity right-click context menu.
 *
 * Per Autodesk SHORTCUTMENU bitmask reference (default 11 = bits 1+2+8):
 *   - bit 1 (1)  → Default mode — no command, no selection.
 *   - bit 2 (2)  → Edit mode — at least one entity selected.
 *   - bit 4 (4)  → Command mode menu always (even with no options).
 *   - bit 8 (8)  → Command mode menu only when current command has options.
 *   - bit 16(16) → time-sensitive (short=Enter, long=menu).
 *
 * This component renders the popup. It does not read or write SHORTCUTMENU
 * itself — the parent (App.tsx) decides which `mode` to pass based on draft
 * session / selection state and the active SHORTCUTMENU bits.
 *
 * Closing rules:
 *   - Click outside the menu → close (`onClose`).
 *   - Esc → close.
 *   - Click on an item → invoke `item.onSelect` then close.
 *
 * Sub-menus:
 *   - One level of nesting via `item.children`. Hovering the parent row
 *     opens the child popup to the right (`.ctx-submenu`).
 *
 * No 3rd-party deps. No portals — the menu lives inside the same DOM tree
 * as the stage so jsdom test rigs can find it via testid lookups.
 */

import { useEffect, useMemo, useRef, useState } from "react";

/**
 * `"osnap"` (W2.5 OSNAP-OVERRIDE-MODES-S2) — AutoCAD's Shift+right-click
 * Object Snap cursor menu during an active point-pick. Distinct from the
 * SHORTCUTMENU-driven `default`/`edit`/`command` modes above (see
 * `useStageContextMenu.handleStageContextMenu`).
 */
export type ContextMenuMode = "default" | "edit" | "command" | "osnap";

export interface ContextMenuItem {
  /** Stable id for testid + React key. */
  readonly id: string;
  /** User-facing label (already i18n-translated by caller). */
  readonly label: string;
  /** Optional accelerator hint shown right-aligned. */
  readonly shortcut?: string;
  /** Optional submenu items. When present, `onSelect` is ignored. */
  readonly children?: ReadonlyArray<ContextMenuItem>;
  /** Click handler. Called with no args; no return value. */
  readonly onSelect?: () => void;
  /** Greyed-out / unclickable. */
  readonly disabled?: boolean;
  /** Visual divider above this item. */
  readonly divider?: boolean;
}

export interface StageContextMenuProps {
  /** Screen-space pixel position where the menu should appear. */
  readonly screenPos: { x: number; y: number };
  /** Mode dictates the title shown at the top of the menu. */
  readonly mode: ContextMenuMode;
  /** Top-of-menu title. Caller already runs through i18n. */
  readonly title: string;
  /** Items list. Caller-built from i18n strings. */
  readonly items: ReadonlyArray<ContextMenuItem>;
  /** Invoked when the menu must close (Esc / outside click / item picked). */
  readonly onClose: () => void;
}

/**
 * AutoCAD-parity right-click popup. Pure presentational — caller wires up
 * the items list per `mode`.
 */
export function StageContextMenu({
  screenPos,
  mode,
  title,
  items,
  onClose,
}: StageContextMenuProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // ── Esc closes ───────────────────────────────────────────────
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // ── Click-outside closes ─────────────────────────────────────
  useEffect(() => {
    function onPointerDown(e: PointerEvent): void {
      const node = containerRef.current;
      if (!node) return;
      const target = e.target as Node | null;
      if (target && node.contains(target)) return;
      onClose();
    }
    // Defer one tick — the right-click that opened the menu lands as a
    // pointerup, but a follow-up pointerdown anywhere else should close.
    const id = window.setTimeout(() => {
      window.addEventListener("pointerdown", onPointerDown);
    }, 0);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("pointerdown", onPointerDown);
    };
  }, [onClose]);

  // Constrain to viewport so the menu doesn't render off-screen near the
  // bottom-right edge. 200×400px envelope covers the largest mode (edit).
  const adjustedPos = useMemo(() => {
    const pad = 8;
    const w =
      typeof window !== "undefined"
        ? window.innerWidth
        : screenPos.x + 220;
    const h =
      typeof window !== "undefined"
        ? window.innerHeight
        : screenPos.y + 420;
    const maxX = Math.max(pad, w - 220);
    const maxY = Math.max(pad, h - 420);
    return {
      x: Math.min(Math.max(screenPos.x, pad), maxX),
      y: Math.min(Math.max(screenPos.y, pad), maxY),
    };
  }, [screenPos]);

  return (
    <div
      ref={containerRef}
      className="drawing-stage-context-menu"
      data-testid="stage-context-menu"
      data-mode={mode}
      style={{ left: adjustedPos.x, top: adjustedPos.y }}
      role="menu"
      aria-label={title}
      // Block native context-menu on the popup itself so a quick double
      // right-click doesn't open another menu over this one.
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="drawing-stage-context-title">{title}</div>
      <div className="drawing-stage-context-list">
        {items.map((item) => (
          <ContextMenuRow
            key={item.id}
            item={item}
            isHovered={hoveredId === item.id}
            onHover={(hovered) => setHoveredId(hovered ? item.id : null)}
            onClose={onClose}
          />
        ))}
      </div>
    </div>
  );
}

interface ContextMenuRowProps {
  readonly item: ContextMenuItem;
  readonly isHovered: boolean;
  readonly onHover: (hovered: boolean) => void;
  readonly onClose: () => void;
}

function ContextMenuRow({
  item,
  isHovered,
  onHover,
  onClose,
}: ContextMenuRowProps) {
  const hasChildren = !!item.children && item.children.length > 0;

  function handleClick(): void {
    if (item.disabled) return;
    if (hasChildren) return;
    item.onSelect?.();
    onClose();
  }

  return (
    <>
      {item.divider ? (
        <div className="drawing-stage-context-divider" aria-hidden="true" />
      ) : null}
      <button
        type="button"
        className="drawing-stage-context-action"
        // Canonical testid: `context-menu-item-<id>` (the pan / zoom command
        // e2e clicks `context-menu-item-pan` for the right-click 'Pan' arm of
        // the cmd-pan slice). testing-library / Playwright `getByTestId` query
        // ONLY `data-testid`, so a `data-testid-legacy` alias is a dead attribute
        // (cmd-pan FINDING 3 — removed; grep confirmed no consumer used the old
        // `stage-context-item-<id>` testid).
        data-testid={`context-menu-item-${item.id}`}
        disabled={!!item.disabled}
        onClick={handleClick}
        onMouseEnter={() => onHover(true)}
        onMouseLeave={() => onHover(false)}
        role={hasChildren ? "menuitem" : "menuitemradio"}
        aria-haspopup={hasChildren ? "menu" : undefined}
      >
        <span>{item.label}</span>
        {hasChildren ? (
          <span className="ctx-arrow" aria-hidden="true">
            ▸
          </span>
        ) : item.shortcut ? (
          <span className="ctx-shortcut">{item.shortcut}</span>
        ) : null}
        {hasChildren && isHovered ? (
          <div
            className="ctx-submenu"
            role="menu"
            data-testid={`stage-context-submenu-${item.id}`}
            // Keep submenu open while pointer is over it.
            onMouseEnter={() => onHover(true)}
            onMouseLeave={() => onHover(false)}
          >
            {item.children!.map((child) => (
              <ContextMenuRow
                key={child.id}
                item={child}
                isHovered={false}
                onHover={() => undefined}
                onClose={onClose}
              />
            ))}
          </div>
        ) : null}
      </button>
    </>
  );
}
